import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  DatePicker,
  Tabs,
  Table,
  Spin,
  Tag,
  Collapse,
  Divider,
  Segmented
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/pro-duotone-svg-icons';
import Button from '../../shared-components/buttons/Button';
import MainPage from '../../shared-components/main-page/MainPage';
import { H1 } from '../../shared-components/typography/Title';
import { userPrivilagesAtom, notificationsAtom, reloadSpecialServicesAtom } from '../../../atoms/Atoms';
import api from '../../../api/api';
import Select from '../../shared-components/form/Select';
import envConfig from '../../../envConfig';
import { thousand } from '../../../functions';

function HmsTrips() {
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const userPrivilages = useRecoilValue(userPrivilagesAtom);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [state, setState] = useState({
    start: null,
    end: null,
    selectedRoute: '',
    selectedVessel: '',
    weekdays: null,
    loading: false,
    page: 0,
    size: 20,
    data: [],
    totalCount: 0,
    trips: [],
    routes: [],
    vessels: []
  });
  const [routeState, setRouteState] = useState({
    routes: [],
    loading: false
  });
  const [vesselState, setVesselState] = useState({
    vessels: [],
    loading: false,
  });
  const [selectedSegemnt, setSelectedSegemnt] = useState(t('completed'));

  function getRoutes() {
    setRouteState({
      routes: [],
      loading: true
    });
    api.get('hms/routes')
    .then((response) => {
      if (response.data.success) {
        setRouteState({
          routes: response.data.data,
          loading: false
        });
      } else {
        setRouteState({
          routes: [],
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setRouteState({
        routes: [],
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function getVessels() {
    setVesselState({
      vessels: [],
      loading: true
    });
    api.get('hms/vessels')
    .then((response) => {
      if (response.data.success) {
        setVesselState({
          vessels: response.data.data,
          loading: false
        });
      } else {
        setVesselState({
            vessels: [],
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
        setVesselState({
        vessels: [],
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function getTypeFromSegment(seg) {
    if (seg === 'on_time') {
      return 1;
    }
    if (seg === 'delayed') {
      return 2;
    }
    if (seg === 'missed') {
      return 3;
    }
    return 0;
  }

  function getData(page) {
    setState({
      ...state,
      page,
      loading: true
    });
    const startDate = dayjs(state.start);
    const endDate = dayjs(state.end);
    const startString = startDate.add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    const endString = endDate.add(4, 'hour').add(1, 'day').add(-1, 'second').format('YYYY-MM-DDTHH:mm:ss');
    api.post('hms/search/trips', {
        ...state,
      selectedRoute: state.selectedRoute,
      selectedVessel: state.selectedVessel,
      weekdend: state.weekdend,
      startDate: startString,
      endDate: endString,
      page,
      size: state.size,
      type: getTypeFromSegment(selectedSegemnt)
    })
    .then((response) => {
      if (response.data.success) {
        setState({
          ...state,
          loading: false,
          totalCount: response.data.data.totalTrips,
          trips: response.data.data.trips,
          routes: response.data.data.routes,
          vessels: response.data.data.vessels
        });
      } else {
        setState({
          ...state,
          loading: false
        });
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response.data.error,
            error: true,
          }
        ]);
      }
    })
    .catch((error) => {
      setState({
        ...state,
        loading: false
      });
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error.message,
          error: true,
        }
      ]);
    })
  }

  function getTrips(analysis) {
    if (selectedSegemnt === t('on_time')) {
      return {
        trips: analysis.onTimeTrips,
        weekday: analysis.onTimeTripsWeekday,
        weekend: analysis.onTimeTripsWeekend
      };
    }
    if (selectedSegemnt === t('delayed')) {
      return {
        trips: analysis.delayedTrips,
        weekday: analysis.delayedTripsWeekday,
        weekend: analysis.delayedTripsWeekend
      };
    }
    if (selectedSegemnt === t('missed')) {
      return {
        trips: analysis.missedTrips,
        weekday: analysis.missedTripsWeekday,
        weekend: analysis.missedTripsWeekend
      };
    }
    return {
      trips: analysis.completedTrips,
      weekday: analysis.completedTripsWeekday,
      weekend: analysis.completedTripsWeekend
    };
  }

  function getPassengers(analysis) {
    if (selectedSegemnt === t('on_time')) {
      return {
        passengers: analysis.onTimePassengers,
        weekday: analysis.onTimeWeekdayPassengers,
        weekend: analysis.onTimeWeekendPassengers
      };
    }
    if (selectedSegemnt === t('delayed')) {
      return {
        passengers: analysis.delayedPassengers,
        weekday: analysis.delayedWeekdayTravelMiles,
        weekend: analysis.delayedWeekendTravelMiles
      };
    }
    if (selectedSegemnt === t('missed')) {
      return {
        passengers: 0,
        weekday: 0,
        weekend: 0
      };
    }
    return {
      passengers: analysis.completedPassengers,
      weekday: analysis.completedWeekdayPassengers,
      weekend: analysis.completedWeekendPassengers
    };
  }

  function getVehicles(analysis) {
    if (selectedSegemnt === t('on_time')) {
      return {
        vehicles: analysis.onTimeVehicles,
        weekday: analysis.onTimeWeekdayVehicles,
        weekend: analysis.onTimeWeekendVehicles
      };
    }
    if (selectedSegemnt === t('delayed')) {
      return {
        vehicles: analysis.delayedVehicles,
        weekday: analysis.delayedWeekdayVehicles,
        weekend: analysis.delayedWeekendVehicles
      };
    }
    if (selectedSegemnt === t('missed')) {
      return {
        vehicles: 0,
        weekday: 0,
        weekend: 0
      };
    }
    return {
      vehicles: analysis.completedVehicles,
      weekday: analysis.completedWeekdayVehicles,
      weekend: analysis.completedWeekendVehicles
    };
  }

  function getTravelTime(analysis) {
    if (selectedSegemnt === t('on_time')) {
      return {
        travelTime: analysis.onTimeTravelTime,
        weekday: analysis.onTimeWeekdayTravelTime,
        weekend: analysis.onTimeWeekendTravelTime
      };
    }
    if (selectedSegemnt === t('delayed')) {
      return {
        travelTime: analysis.delayedTravelTime,
        weekday: analysis.delayedWeekdayTravelTime,
        weekend: analysis.delayedWeekendTravelTime
      };
    }
    if (selectedSegemnt === t('missed')) {
      return {
        travelTime: 0,
        weekday: 0,
        weekend: 0
      };
    }
    return {
      travelTime: analysis.completedTravelTime,
      weekday: analysis.completedWeekdayTravelTime,
      weekend: analysis.completedWeekendTravelTime
    };
  }

  function getTravelMiles(analysis) {
    if (selectedSegemnt === t('on_time')) {
      return {
        travelMiles: analysis.onTimeTravelMiles,
        weekday: analysis.onTimeWeekdayTravelMiles,
        weekend: analysis.onTimeWeekendTravelTMiles
      };
    }
    if (selectedSegemnt === t('delayed')) {
      return {
        travelMiles: analysis.delayedTravelMiles,
        weekday: analysis.delayedWeekdayTravelMiles,
        weekend: analysis.delayedWeekendTravelMiles
      };
    }
    if (selectedSegemnt === t('missed')) {
      return {
        travelMiles: 0,
        weekday: 0,
        weekend: 0
      };
    }
    return {
      travelMiles: analysis.completedTravelMiles,
      weekday: analysis.completedWeekdayTravelMiles,
      weekend: analysis.completedWeekendTravelMiles
    };
  }

  useEffect(() => {
    setState({
      ...state,
      start: dayjs().startOf('month').format('MMM DD, YYYY'),
      end: dayjs().endOf('month').format('MMM DD, YYYY'),
      size: 20,
      page: 0,
      selectedRoute: '',
      selectedVessel: '',
      weekdays: null,
      trips: [],
      routes: [],
      vessels: []
    });
    getVessels();
    getRoutes();
  }, []);

  useEffect(() => {
    if (state?.start && state?.end) {
      getData(0);
    }
  }, [state?.start, state?.end, state?.size, state?.selectedRoute, state?.selectedVessel, state?.weekend]);

  const analysisColumns = [
    {
      title: '',
      align: 'center',
      className: 'text-sm md:hidden',
      render: (row) => (
        <div className="grid grid-cols-2">
          <p className="col-span-2 font-bold text-lg">{row.label}</p>
          <div className="col-span-2 border p-2">
            <p>{t('trips')}</p>
            <p className="font-bold text-md">{thousand(getTrips(row).trips)}</p>
            <div className="flex mt-4">
              <span>{thousand(getTrips(row).weekday)}</span>
              <span className="ml-auto">{thousand(getTrips(row).weekend)}</span>
            </div>
            <div className="flex">
            <span>{t('weekday')}</span>
            <span className="ml-auto">{t('weekend')}</span>
            </div>
          </div>
          
          <div className="col-span-1 border p-2">
            <p>{t('passengers')}</p>
            <p className="font-bold text-md">{thousand(getPassengers(row).passengers)}</p>
            <div className="flex mt-4">
              <span>{thousand(getPassengers(row).weekday)}</span>
              <span className="ml-auto">{thousand(getPassengers(row).weekend)}</span>
            </div>
            <div className="flex">
            <span>{t('weekday')}</span>
            <span className="ml-auto">{t('weekend')}</span>
            </div>
          </div>

          <div className="col-span-1 border p-2">
            <p>{t('vehicles')}</p>
            <p className="font-bold text-md">{thousand(getVehicles(row).vehicles)}</p>
            <div className="flex mt-4">
              <span>{thousand(getVehicles(row).weekday)}</span>
              <span className="ml-auto">{thousand(getVehicles(row).weekend)}</span>
            </div>
            <div className="flex">
            <span>{t('weekday')}</span>
            <span className="ml-auto">{t('weekend')}</span>
            </div>
          </div>

          <div className="col-span-1 border p-2">
            <p>{t('travel_time')} (hr)</p>
            <p className="font-bold text-md">{thousand(getTravelTime(row).travelTime)}</p>
            <div className="flex mt-4">
              <span>{thousand(getTravelTime(row).weekday)}</span>
              <span className="ml-auto">{thousand(getTravelTime(row).weekend)}</span>
            </div>
            <div className="flex">
            <span>{t('weekday')}</span>
            <span className="ml-auto">{t('weekend')}</span>
            </div>
          </div>

          <div className="col-span-1 border p-2">
            <p>{t('travel_miles')}</p>
            <p className="font-bold text-md">{thousand(getTravelMiles(row).travelMiles)}</p>
            <div className="flex mt-4">
              <span>{thousand(getTravelMiles(row).weekday)}</span>
              <span className="ml-auto">{thousand(getTravelMiles(row).weekend)}</span>
            </div>
            <div className="flex">
            <span>{t('weekday')}</span>
            <span className="ml-auto">{t('weekend')}</span>
            </div>
          </div>

        </div>
      )
    },
    {
      title: t('vessel'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <span>{row.label}</span>
      ),
    },
    {
      title: t('trips'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p className="font-bold text-md">{thousand(getTrips(row).trips)}</p>
          <div className="flex mt-4">
            <span>{thousand(getTrips(row).weekday)}</span>
            <span className="ml-auto">{thousand(getTrips(row).weekend)}</span>
          </div>
          <div className="flex">
          <span>{t('weekday')}</span>
          <span className="ml-auto">{t('weekend')}</span>
          </div>
        </div>
      ),
    },
    {
      title: t('passengers'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p className="font-bold text-md">{thousand(getPassengers(row).passengers)}</p>
          <div className="flex mt-4">
            <span>{thousand(getPassengers(row).weekday)}</span>
            <span className="ml-auto">{thousand(getPassengers(row).weekend)}</span>
          </div>
          <div className="flex">
          <span>{t('weekday')}</span>
          <span className="ml-auto">{t('weekend')}</span>
          </div>
        </div>
      ),
    },
    {
      title: t('vehicles'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p className="font-bold text-md">{thousand(getVehicles(row).vehicles)}</p>
          <div className="flex mt-4">
            <span>{thousand(getVehicles(row).weekday)}</span>
            <span className="ml-auto">{thousand(getVehicles(row).weekend)}</span>
          </div>
          <div className="flex">
          <span>{t('weekday')}</span>
          <span className="ml-auto">{t('weekend')}</span>
          </div>
        </div>
      ),
    },
    {
      title: `${t('travel_time')} (hr)`,
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p className="font-bold text-md">{thousand(getTravelTime(row).travelTime)}</p>
          <div className="flex mt-4">
            <span>{thousand(getTravelTime(row).weekday)}</span>
            <span className="ml-auto">{thousand(getTravelTime(row).weekend)}</span>
          </div>
          <div className="flex">
          <span>{t('weekday')}</span>
          <span className="ml-auto">{t('weekend')}</span>
          </div>
        </div>
      ),
    },
    {
      title: t('travel_miles'),
      align: 'center',
      className: 'text-sm hidden md:table-cell',
      render: (row) => (
        <div>
          <p className="font-bold text-md">{thousand(getTravelMiles(row).travelMiles)}</p>
          <div className="flex mt-4">
            <span>{thousand(getTravelMiles(row).weekday)}</span>
            <span className="ml-auto">{thousand(getTravelMiles(row).weekend)}</span>
          </div>
          <div className="flex">
          <span>{t('weekday')}</span>
          <span className="ml-auto">{t('weekend')}</span>
          </div>
        </div>
      ),
    }
  ];

  return (
    <MainPage>
      <H1 text={`HMS - ${t('trips')}`} />
      <div className="grid gap-2 grid-cols-1 md:grid-cols-2 lg:grid-cols-6">
        <Select
          className="col-span-1 lg:col-start-2"
          label={t('day_type')}
          id="weekend"
          name="weekend"
          disabled={state.loading}
          value={state.weekend}
          onChange={(obj) => {
            setState({
              ...state,
              weekend: obj.target.value
            });
          }}
          options={[
            { key: 'both', value: null, text: t('both') },
            { key: 'weekday', value: false, text: t('weekday') },
            { key: 'weekend', value: true, text: t('weekend') }
          ]}
        />
        <Select
          className="col-span-1"
          label={t('route')}
          id="selectedRoute"
          name="selectedRoute"
          value={state.selectedRoute}
          onChange={(obj) => {
            setState({
              ...state,
              selectedRoute: obj.target.value
            });
          }}
          loading={routeState.loading}
          disabled={routeState.loading || state.loading}
          options={[{ key: 'any', value: '', text: t('any') }, ..._.map(routeState.routes, (route) => ({ key: route, value: route, text: route }))]}
        />
        <Select
          className="col-span-1"
          label={t('vessel')}
          id="selectedVessel"
          name="selectedVessel"
          value={state.selectedVessel}
          onChange={(obj) => {
            setState({
              ...state,
              selectedVessel: obj.target.value
            });
          }}
          loading={vesselState.loading}
          disabled={vesselState.loading || state.loading}
          options={[{ key: 'any', value: '', text: t('any') }, ..._.map(vesselState.vessels, (vessel) => ({ key: vessel, value: vessel, text: vessel }))]}
        />
        <RangePicker
          value={[
            dayjs(state?.start),
            dayjs(state?.end)
          ]}
          disabled={state.loading}
          className="h-11 bg-transparent col-span-1"
          onChange={(obj) => {
            setState({
              ...state,
              start: obj[0],
              end: obj[1]
            });
          }}
          format="MMM DD, YYYY"
        />
        <Button
          className="bg-primaryColor px-2 col-span-1 md:col-start-2 lg:col-start-6"
          textColor="text-primaryTextColor"
          loaderColor="bg-primaryTextColor"
          borderColor="border-primaryBorderColor"
          text={t('refresh')}
          loading={state.loading}
          onClick={() => getData()}
        />
      </div>
      <div>
        <Segmented
          size="large"
          block
          className="w-full mt-4 space-x-2"
          options={[t('completed'), t('on_time'), t('delayed'), t('missed')]}
          value={selectedSegemnt}
          onChange={(value) => {
            setSelectedSegemnt(value);
          }}
        />
      </div>
      {/* <Divider orientation="left">{t('routes')}</Divider> */}
      <Collapse
        className="mt-2"
        items={[{
          key: t('routes'),
          label: t('routes'),
          children: (
            <Table
              bordered
              dataSource={state.routes}
              loading={state.loading}
              rowKey="label"
              className="mt-2"
              locale={{
                emptyText: t('no_data')
              }}
              pagination={false}
              columns={analysisColumns}
            />
          )
        }]}
      />

      {/* <Divider orientation="left">{t('vessels')}</Divider> */}
      <Collapse
        className="mt-2"
        items={[{
          key: t('vessels'),
          label: t('vessels'),
          children: (
            <Table
              bordered
              dataSource={state.vessels}
              loading={state.loading}
              rowKey="label"
              className="mt-2"
              locale={{
                emptyText: t('no_data')
              }}
              pagination={false}
              columns={analysisColumns}
            />
          )
        }]}
      />

      {/* <Divider orientation="left">{t('trips')}</Divider> */}
      <Divider />
      <Collapse
        className="mt-2"
        items={[{
          key: t('trips'),
          label: t('trips'),
          children: (
            <Table
              bordered
              dataSource={state.trips}
              loading={state.loading}
              rowKey="id"
              className="mt-2"
              locale={{
                emptyText: t('no_data')
              }}
              pagination={{
                pageSize: state.size,
                showSizeChanger: true,
                current: state.page + 1,
                total: state?.totalCount,
                onChange: (page, size) => {
                  if (page - 1 !== state?.page) {
                    getData(page);
                  } else {
                    setState({
                      ...state,
                      size
                    });
                  }
                }
              }}
              columns={[
                {
                  title: '',
                  align: 'center',
                  className: 'text-sm md:hidden',
                  render: (row) => (
                    <div className="grid gap-2 grid-cols-2">
                      <div className="col-span-2">
                        <p className="col-span-2">{row.vessel}</p>
                        <p>{row.route}</p>
                        <p>{row.origin} <FontAwesomeIcon icon={faArrowRight} /> {row.destination}</p>
                      </div>
                      <div className="col-span-2">
                        <p>{t('scheduled_departure_time')}</p>
                        <p>{dayjs(row.scheduled_Departure_Time).format('MMM DD, YYYY hh:mm A')}</p>
                      </div>
                      <div className="col-span-1 text-left">
                        <p>{t('actual_departure_time')}</p>
                        <p>{row.actual_Departure_Time ? dayjs(row.actual_Departure_Time).format('hh:mm A') : '-'}</p>
                      </div>
                      <div className="col-span-1 text-right">
                        <p>{t('arrival_time')}</p>
                        <p>{row.arrival_Time ? dayjs(row.arrival_Time).format('hh:mm A') : '-'}</p>
                      </div>
                      <div className="col-span-1 text-left">
                        <p>{t('travel_time')} (hr)</p>
                        <span>{thousand(row.travel_Time_Hours)}</span>
                      </div>
                      <div className="col-span-1 text-right">
                        <p>{t('travel_miles')}</p>
                        <span>{thousand(row.miles)}</span>
                      </div>
                      <div className="col-span-1 text-left">
                        <p>{t('passengers')}</p>
                        <span>{thousand(row.pax)}</span>
                      </div>
                      <div className="col-span-1 text-right">
                        <p>{t('vehicles')}</p>
                        <span>{thousand(row.vehicles)}</span>
                      </div>
                      <div className="col-span-1 text-left">
                        <p>{t('status')}</p>
                        <span>{row.trip_Status}</span>
                      </div>
                      <div className="col-span-1 text-right">
                        <p>{t('OTP')}</p>
                        <span>{row.trip_Status === 'COMPLETED' ? row.otp : '-'}</span>
                      </div>
                    </div>
                  )
                },
                {
                  title: t('vessel'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.vessel}</span>
                  ),
                },
                {
                  title: t('route'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <div>
                      <p>{row.route}</p>
                      <p>{row.origin} <FontAwesomeIcon icon={faArrowRight} /> {row.destination}</p>
                    </div>
                  ),
                },
                {
                  title: t('scheduled_departure_time'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{dayjs(row.scheduled_Departure_Time).format('MMM DD, YYYY hh:mm A')}</span>
                  ),
                },
                {
                  title: t('actual_departure_time'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.actual_Departure_Time ? dayjs(row.actual_Departure_Time).format('hh:mm A') : '-'}</span>
                  ),
                },
                {
                  title: t('arrival_time'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.arrival_Time ? dayjs(row.arrival_Time).format('hh:mm A') : '-'}</span>
                  ),
                },
                {
                  title: `${t('travel_time')} (hr)`,
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.travel_Time_Hours)}</span>
                  ),
                },
                {
                  title: t('travel_miles'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.miles)}</span>
                  ),
                },
                {
                  title: t('passengers'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.pax)}</span>
                  ),
                },
                {
                  title: t('vehicles'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{thousand(row.vehicles)}</span>
                  ),
                },
                {
                  title: t('status'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.trip_Status}</span>
                  ),
                },
                {
                  title: t('OTP'),
                  align: 'center',
                  className: 'text-sm hidden md:table-cell',
                  render: (row) => (
                    <span>{row.trip_Status === 'COMPLETED' ? row.otp : '-'}</span>
                  ),
                },
              ]}
            />
          )
        }]}
      />
    </MainPage>
  )
}

export default HmsTrips;
